import { AiAssistantButton } from '@/components/GenAI/AiAssistantButton';
import { AiAssistantDrawer } from '@/components/GenAI/AiAssistantDrawer';
import { parseSystemDesignPayload } from '@/helpers/parseSystemDesignPayload';
import { useGetDrawerMarginStyles } from '@/hooks/useGetDrawerMarginStyles';
import { useParentDivLayoutStyles } from '@/hooks/useParentDivLayoutStyles';
import { useProjectSubmittedBefore2026 } from '@/hooks/useProjectSubmittedBefore2026';
import { isErrorWithData } from '@/services/helpers';
import { useGetQueueByAccountIdAndTypeQuery } from '@/services/queues';
import { Origin } from '@/types/ProductionValueCheck';
import { Box, Button, Heading, toast, useOpenClose } from '@palmetto/palmetto-components';
import { Formik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import {
  AttestmentTypeLabelMap,
  DomesticContentAttestmentType,
  DomesticContentEligibilityLabels,
  DomesticContentEligibilityTypes,
  type Milestone,
  MilestoneRequirement,
  MilestoneStatus,
  MilestoneType,
  MountingType,
  type ProgramType,
  QueueStatus,
  QueueType,
  type SystemDesign,
  UserPermissions,
} from 'types';

import { useSetPvSystemInitialValues } from '../../helpers/setPvSystemInitialValues';
import { useEditAccountMutation, useGetAccountQuery } from '../../services/accounts';
import { useGetCurrentContractQuery } from '../../services/contract';
import { useGetQuotesQuery } from '../../services/quotes';
import { useCreateSystemDesignMutation, useGetCurrentDesignQuery } from '../../services/systemDesign';
import { useAuth } from '../auth/authProvider';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import { isInternalUser } from '../auth/RequireIsInternalUser';
import RequirePalmettoFinanceUser, { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import RequirePermissions from '../auth/requirePermissions';
import { EnhancedFormikForm } from '../EnhancedFormikForm';
import FormSectionContainer from '../Forms/FormSectionContainer';
import { AlertProductionDifference } from '../SystemDesign/AlertProductionDifference';
import { DomesticContentMountingHardwareSection } from '../SystemDesign/DomesticContentMountingHardwareSection';
import { EquipmentDetailsFormSection } from '../SystemDesign/EquipmentDetailsFormSection';
import { MonthlyProductionFormSection } from '../SystemDesign/MonthlyProductionFormSection';
import { MountingHardwareFormSection } from '../SystemDesign/MountingHardwareFormSection';
import { SolarArraysFormSection } from '../SystemDesign/SolarArraysFormSection';
import { SystemSizeAndFirstYearProductionSections } from '../SystemDesign/SystemSizeAndFirstYearProductionSections';

import { PropertyBox } from './components/PropertyBox';
import { Sidebar } from './components/Sidebar';
import { SidebarSection } from './components/SidebarSection';

/** has default values for threshold but can be moved to use appSettings in the future */
const isChangeWithinThreshold = ({
  oldValue,
  newValue,
  minPercent = -0.05,
  maxPercent = 0.1,
}: {
  oldValue: number;
  newValue: number;
  minPercent?: number;
  maxPercent?: number;
}): boolean => {
  const percentChange = (newValue - oldValue) / oldValue;
  return percentChange >= minPercent && percentChange <= maxPercent;
};

const avlEntrySchema = yup.object({
  manufacturer: yup.string().optional().nullable(),
  model: yup.string().optional().nullable(),
  count: yup.number().optional().nullable(),
});

const inverterSchema = yup.object({
  model: yup.string().optional().nullable(),
  count: yup.number().optional().nullable(),
  vintage: yup.string().optional().nullable(),
});

const solarArraySchema = yup.object({
  panelCount: yup
    .number()
    .min(0)
    .optional()
    .nullable()
    .label('Panel Count')
    .typeError('Panel Count must be a number'),
  annualTSRF: yup
    .number()
    .min(0)
    .max(100)
    .optional()
    .nullable()
    .label('Annual TSRF')
    .typeError('Annual TSRF must be a number'),
  tilt: yup.number().min(0).max(90).optional().nullable().label('Tilt').typeError('Tilt must be a number'),
  azimuth: yup.number().min(0).max(360).optional().nullable().label('Azimuth').typeError('Azimuth must be a number'),
  inverter: yup.number().optional().nullable().label('Inverter').typeError('Inverter must be a number'),
  azimuthUnit: yup.string().optional().nullable().default('degrees').typeError('Azimuth unit must be a string'),
  tiltUnit: yup.string().optional().nullable().default('degrees').typeError('Tilt unit must be a string'),
  monthlySolarAccess: yup
    .mixed()
    .test({
      test: function (value: any) {
        if (value === undefined) return true;
        for (let i = 0; i < 12; i++) {
          const val = value[i];
          if (!val) {
            return this.createError({ message: 'All monthly solar access values must be filled out' });
          }
        }
        return true;
      },
    })
    .label('Monthly Solar Access'),
});

export const useGetPVSystemSchema = () => {
  const { claims } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);

  return yup.object({
    systemSizeKw: yup
      .mixed()
      .test({
        test: function (value: unknown) {
          if (!value) return true;
          const numberValue = Number(value);
          if (isNaN(numberValue)) {
            return this.createError({ message: 'System size must be a number' });
          }
          if (numberValue < 0) {
            return this.createError({ message: 'System size must be a positive number' });
          }
          return true;
        },
      })
      .required('Required for quote creation'),
    systemFirstYearProductionKwh: yup
      .mixed()
      .test({
        test: function (value: unknown) {
          if (!value) return true;
          const numberValue = Number(value);
          if (isNaN(numberValue)) {
            return this.createError({ message: 'Production must be a number' });
          }
          if (numberValue < 0) {
            return this.createError({ message: 'Production must be a positive number' });
          }
          return true;
        },
      })
      .required('Required for quote creation'),
    panelManufacturer: yup.string().optional().nullable(),
    panelModel: yup.string().optional().nullable(),
    totalPanelCount: yup.number().optional().nullable(),
    inverterManufacturer: yup.string().optional().nullable(),
    inverterModels: yup.array().of(inverterSchema).optional().nullable(),
    storage: yup.array().of(avlEntrySchema).optional().nullable(),
    mountingType: yup.string().optional().default(MountingType.roof),
    mountingManufacturer: yup.string().optional().nullable(),
    firstYearMonthlyProductionKwh: yup
      .mixed()
      // .test({  // Note: Temporarily disabling this validation to unblock contract vendors doing data entry for installed systems.
      //   test: function (value: any) {
      //     if (!(value?.length > 0)) return true;
      //     const sum = (value ?? []).reduce((acc: number, val: string) => (acc ?? 0) + Number(val ?? 0), 0) ?? 0;
      //     const systemFirstYearProductionKwh = this.parent.systemFirstYearProductionKwh;
      //     if (Math.abs(sum - systemFirstYearProductionKwh) > 0.01) {
      //       const message = `Monthly production values sum ${sum.toFixed(
      //         2,
      //       )} kWh does not match expected sum ${systemFirstYearProductionKwh.toFixed(2)} kWh`;
      //       return this.createError({ message });
      //     }
      //     return true;
      //   },
      // })
      .optional()
      .nullable(),
    ...(isFinCoUser
      ? {
          arrays: yup.array().of(solarArraySchema).optional().nullable(),
        }
      : {}),
  });
};

export function hasInstallMilestoneApproved(milestones: Milestone[]) {
  return (
    milestones.find((milestone: Milestone) => milestone.type === MilestoneType.install)?.status ===
    MilestoneStatus.approved
  );
}
export function hasInstallMilestoneCompleted(milestones: Milestone[]) {
  return !!milestones.find((milestone: Milestone) => milestone.type === MilestoneType.install)?.completedAt;
}

const calculateSystemDesignReadOnlyStatus = ({
  isInternalUser,
  isDomesticContentEligible,
  systemDesign,
  hasInstallPackageQueueBeenSubmittedForReview,
}: {
  isInternalUser: boolean;
  isDomesticContentEligible: boolean;
  systemDesign: SystemDesign;
  hasInstallPackageQueueBeenSubmittedForReview: boolean;
}): boolean => {
  const hasDomesticContentAttestment = Boolean(systemDesign?.domesticContentAttestment);
  const isDomesticContentOptedOut =
    systemDesign?.domesticContentAttestment?.attestmentType === DomesticContentAttestmentType.optOut;

  // If domestic content is not eligible or not attested then the system design is not read-only
  if (!isDomesticContentEligible || !hasDomesticContentAttestment) {
    return false;
  }

  // For internal users, system design is read-only only if DC is not opted out and the install package queue has been submitted
  // for review
  if (isInternalUser) {
    return hasInstallPackageQueueBeenSubmittedForReview && !isDomesticContentOptedOut;
  }

  return hasInstallPackageQueueBeenSubmittedForReview;
};

const PVSystem = () => {
  const { systemSizeChangeCalculator = false } = useFlags();
  const navigate = useNavigate();
  const { id } = useParams<{ id: any }>();
  const { claims } = useAuth();
  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(id);
  const programType = account?.programType;
  const { data: systemDesign, isLoading: isDesignLoading } = useGetCurrentDesignQuery(
    { id, programType },
    { skip: !programType },
  );
  const isItcSystemDesignCompleted = Boolean(systemDesign?.itcQualification);
  const isLoading = isDesignLoading || isAccountLoading;
  const hasApprovedContract = useMemo(() => {
    const milestoneRequirements = account?.milestones.find(
      (milestone: any) => milestone.type === MilestoneType.noticeToProceed,
    )?.requirements;
    return (
      milestoneRequirements?.find((requirement: any) => requirement.type === MilestoneRequirement.contractSigned)
        ?.status === MilestoneStatus.approved
    );
  }, [account?.milestones]);
  const [createSystemDesign] = useCreateSystemDesignMutation();
  const [updateAccount] = useEditAccountMutation();
  const [productionDifferenceIfExceedsMargin, setProductionDifferenceIfExceedsMargin] = useState<number>(0);
  const initialValues = useSetPvSystemInitialValues(account);
  const allowsAction = useIsOrgReadOnly();
  const validationSchema = useGetPVSystemSchema();
  const targetRef = useRef(null);
  const {
    isOpen: isModelOutputsDrawerOpen,
    handleClose: closeModelOutputsDrawer,
    handleToggle: toggleModelOutputsDrawer,
  } = useOpenClose();
  const parentStyles = useParentDivLayoutStyles(targetRef);
  const drawerMarginStyles = useGetDrawerMarginStyles(parentStyles, isModelOutputsDrawerOpen, 'left');

  const handleSubmit = useCallback(
    async (values: any, { setSubmitting }: any) => {
      if (
        hasApprovedContract &&
        !isChangeWithinThreshold({
          oldValue: account.systemDesign.systemFirstYearProductionKwh,
          newValue: values.systemFirstYearProductionKwh,
        })
      ) {
        setProductionDifferenceIfExceedsMargin(
          (values.systemFirstYearProductionKwh - account.systemDesign.systemFirstYearProductionKwh) /
            account.systemDesign.systemFirstYearProductionKwh,
        );
        return;
      } else {
        setProductionDifferenceIfExceedsMargin(0);
      }
      try {
        const sentPayload = {
          accountId: id,
          systemDesign: parseSystemDesignPayload(values),
          programType: programType as ProgramType,
        };
        await validationSchema.validate(sentPayload.systemDesign);
        await createSystemDesign(sentPayload).unwrap();
        // Only update the account if the usage has been updated from our UI here. Otherwise nothing has changed and don't need to update
        if (values.electricityUsageKwh && values.electricityUsageUpdated) {
          if (Array.isArray(values.electricityUsageKwh) && values.electricityUsageKwh.length === 12) {
            await updateAccount({
              id,
              account: {
                electricityUsageKwh: values.electricityUsageKwh,
                electricityUsageSource: values.electricityUsageSource,
              },
            });
          } else {
            toast.error('Please provide electricity usage for all 12 months.');
          }
        }
        toast.success('Account PV System Details Saved');
      } catch (e: any) {
        if (e instanceof yup.ValidationError) {
          toast.error(e?.message || 'Error saving account PV system details');
        } else if (isErrorWithData(e)) {
          toast.error(e?.data?.message || 'Error saving account PV system details');
        } else {
          console.error(e);
          toast.error('Error saving account PV system details');
        }
      }
      setSubmitting(false);
    },
    [
      hasApprovedContract,
      account?.systemDesign?.systemFirstYearProductionKwh,
      id,
      validationSchema,
      createSystemDesign,
      programType,
      updateAccount,
    ],
  );

  const { data: quotes } = useGetQuotesQuery({ id, programType }, { skip: !programType });
  const installMilestoneApproved = useMemo(
    () => hasInstallMilestoneApproved(account?.milestones || []),
    [account?.milestones],
  );

  const installed = useMemo(() => hasInstallMilestoneCompleted(account?.milestones || []), [account?.milestones]);
  const { data: contract } = useGetCurrentContractQuery(id, { skip: !id });
  const { data: installPackageQueue } = useGetQueueByAccountIdAndTypeQuery(
    { accountId: id, type: QueueType.installPackage },
    { skip: !id },
  );
  const contractActiveQuote = quotes?.find(
    (element: any) => element.id === contract?.quoteId && element.status !== 'voided',
  );
  const firstYearContracted = contractActiveQuote?.systemFirstYearProductionKwh || 0;
  const systemSizeContracted = contractActiveQuote?.systemSizeKw || 0;
  const firstYearInstalled =
    systemDesign?.systemFirstYearProductionKwh || account?.systemDesign?.systemFirstYearProductionKwh || 0;
  const systemSizeInstalled = systemDesign?.systemSizeKw || account?.systemDesign?.systemSizeKw || 0;

  const pvSystemSchema = useGetPVSystemSchema();

  const isDomesticContentEligible = account?.itcAdderQualifications?.domesticContentEligible;
  const domesticContentEligibilityStatus = account?.domesticContentEligibilityStatus ?? '';
  const dcAttestmentType = systemDesign?.domesticContentAttestment?.attestmentType;
  const dcAttestmentTypeLabel = dcAttestmentType
    ? ` - ${AttestmentTypeLabelMap[dcAttestmentType as keyof typeof AttestmentTypeLabelMap]}`
    : '';

  const showDomesticContent =
    isDomesticContentEligible && domesticContentEligibilityStatus !== DomesticContentEligibilityTypes.epcOptOut;
  const showDcHardwareSection =
    showDomesticContent && dcAttestmentType !== DomesticContentAttestmentType.storageOnly;
  const hasInstallPackageQueueBeenSubmittedForReview =
    installPackageQueue && installPackageQueue.status !== QueueStatus.rejected;

  const isUserInternal = isInternalUser(claims, true);
  const isSystemDesignReadOnly = calculateSystemDesignReadOnlyStatus({
    isInternalUser: isUserInternal,
    isDomesticContentEligible,
    systemDesign,
    hasInstallPackageQueueBeenSubmittedForReview,
  });

  const projectSubmittedBefore2026 = useProjectSubmittedBefore2026(id);
  const isFeocReadOnly = !projectSubmittedBefore2026;
  const isDcEligible =
    domesticContentEligibilityStatus === DomesticContentEligibilityTypes.underConsideration ||
    domesticContentEligibilityStatus === DomesticContentEligibilityTypes.confirmed;

  return (
    <Box
      borderWidth="xs"
      borderColor="separator"
      overflow="hidden"
      direction={{ base: 'column', desktop: 'row' }}
      width="100"
      radius="md"
      ref={targetRef}
      style={{
        marginLeft: drawerMarginStyles,
        transition: 'margin-left 0.3s ease',
      }}
    >
      <Sidebar>
        <Box fontSize="lg" fontWeight="bold">
          PV System Design
        </Box>
        <SidebarSection>
          <Box alignItems="flex-start">
            <Heading as="h5">Active Quote</Heading>
            {contractActiveQuote ? (
              <Box as="p" color="body-secondary" fontSize="sm">
                System size and production entered during quote and contract phase. Used in pricing and sizing
                estimates.
              </Box>
            ) : (
              <Box fontSize="lg">--</Box>
            )}
          </Box>
          <Box>
            <PropertyBox title={contractActiveQuote ? 'Contracted System Size' : 'Expected DC System Size'} noBorder>
              <Box fontSize="lg">
                {contractActiveQuote
                  ? (contractActiveQuote?.systemSizeKw ?? '-- ')
                  : (systemDesign?.systemSizeKw ?? account?.systemDesign?.systemSizeKw ?? '-- ')}
                kW
              </Box>
            </PropertyBox>
            <PropertyBox
              title={contractActiveQuote ? 'Contracted Year-1 Production' : 'Expected Year-1 Production'}
              noBorder
            >
              <Box fontSize="lg">
                {contractActiveQuote
                  ? (contractActiveQuote?.systemFirstYearProductionKwh?.toFixed(0) ?? '--')
                  : ((
                      systemDesign?.systemFirstYearProductionKwh ??
                      account?.systemDesign?.systemFirstYearProductionKwh
                    )?.toFixed(0) ?? '-- ')}
                kWh
              </Box>
            </PropertyBox>
            {!systemSizeChangeCalculator && (
              <PropertyBox title="Total Panel Count" noBorder>
                <Box fontSize="lg">{systemDesign?.totalPanelCount || account?.systemDesign?.panelCount || '-'}</Box>
              </PropertyBox>
            )}
          </Box>
        </SidebarSection>
        {isDomesticContentEligible && (
          <>
            <Box fontSize="lg" fontWeight="bold" padding="xl 0 lg 0">
              Domestic Content
            </Box>
            <SidebarSection>
              <PropertyBox title="Domestic Content Eligibility" noBorder>
                <Box fontSize="lg">
                  {
                    DomesticContentEligibilityLabels[
                      domesticContentEligibilityStatus as keyof typeof DomesticContentEligibilityLabels
                    ]
                  }
                  {(domesticContentEligibilityStatus === DomesticContentEligibilityTypes.confirmed ||
                    domesticContentEligibilityStatus === DomesticContentEligibilityTypes.underConsideration) &&
                    dcAttestmentTypeLabel}
                </Box>
              </PropertyBox>
            </SidebarSection>
          </>
        )}
      </Sidebar>
      <Box width={{ base: '100', desktop: '70' }} childGap="lg" padding={{ base: '0', desktop: 'xl' }}>
        <Formik
          initialValues={initialValues}
          validationSchema={pvSystemSchema}
          onSubmit={handleSubmit}
          enableReinitialize
          validateOnChange={false}
        >
          {({ isSubmitting }) => (
            <EnhancedFormikForm noValidate>
              <Box
                as="h2"
                fontWeight="medium"
                fontSize="lg"
                padding={{ base: 'lg lg 0 lg', desktop: '0' }}
                childGap={{ base: 'md', desktop: 'xl' }}
                gap={{ base: 'md', desktop: 'sm' }}
              >
                System Details
                {contractActiveQuote && (
                  <Box as="p" color="body-secondary" fontSize="sm">
                    <span>
                      <b>Contracted</b> size and production is used for pricing. <br />
                      <b>Expected</b> size should reflect the current design, but may differ from the Contracted
                      size. Changing the Expected size does not impact pricing or the customer contract. Once Quote
                      exists, use Change Order Calculator to update Expected values. <br />
                    </span>
                  </Box>
                )}
              </Box>
              <FormSectionContainer>
                <SystemSizeAndFirstYearProductionSections
                  installMilestoneApproved={installMilestoneApproved}
                  hasCurrentContract={contract && contractActiveQuote}
                  installed={installed}
                  isLoading={isLoading}
                  accountId={id}
                  firstYearInstalledKwH={firstYearInstalled}
                  firstYearContractedKwH={firstYearContracted}
                  systemSizeInstalledKw={systemSizeInstalled}
                  systemSizeContractedKw={systemSizeContracted}
                  asBuiltSystemSizeKw={systemDesign?.asBuiltSystemSizeKw || ''}
                  systemSizeChangeCalculator={systemSizeChangeCalculator}
                />
                {programType ? (
                  <>
                    {(isFeocReadOnly || isUserInternal) && (
                      <Box
                        padding={{ desktop: 'md 0 0 0', base: 'md lg 0 lg' }}
                        childGap={{ base: 'md', desktop: 'xl' }}
                        gap={{ base: 'md', desktop: 'xl' }}
                        direction={{
                          base: 'column',
                          desktop: 'row',
                        }}
                      >
                        <Box
                          width={{
                            desktop: '2xl',
                            hd: '3xl',
                          }}
                          style={{
                            flexShrink: 0,
                          }}
                        />
                        <Box childGap="xs" display="block" width="100">
                          {isFeocReadOnly && (
                            <>
                              <Box>
                                You may add or update only the Panel and Inverter information below. Doing so will
                                not check for System FEOC Compliance, and your team must provide a complete system
                                design including Mounting Hardware to confirm Palmetto LightReach will fund this
                                project.
                              </Box>
                              <Box>
                                To update the system design and calculate FEOC Compliance
                                {isDcEligible ? ' and Domestic Content Adder eligibility' : ''}, click the button
                                below.
                              </Box>
                            </>
                          )}
                          <Button
                            onClick={() => {
                              navigate(`/accounts/${account?.id}/itc-system-design`, {
                                state: {
                                  from: Origin.PV_SYSTEM_TAB,
                                },
                              });
                            }}
                            size="sm"
                            variant="secondary"
                          >
                            Update System
                          </Button>
                        </Box>
                      </Box>
                    )}
                    <EquipmentDetailsFormSection
                      isLoading={isLoading}
                      programType={programType}
                      showOptimizerCheckbox={false}
                      readOnly={isSystemDesignReadOnly}
                      equipmentException={account?.equipmentException}
                      orgAlias={account?.organizationId}
                      useSearchableModelSelect
                    />
                    {showDcHardwareSection ? (
                      <DomesticContentMountingHardwareSection
                        isLoading={isLoading}
                        programType={programType}
                        readOnly={isSystemDesignReadOnly}
                        isItcSystemDesignCompleted={isItcSystemDesignCompleted}
                        orgAlias={account?.organizationId}
                      />
                    ) : (
                      <MountingHardwareFormSection
                        isLoading={isLoading}
                        programType={programType}
                        hideMountingType
                        isItcSystemDesignCompleted={isItcSystemDesignCompleted}
                        orgAlias={account?.organizationId}
                      />
                    )}
                  </>
                ) : (
                  <></>
                )}

                <RequirePalmettoFinanceUser>
                  <MonthlyProductionFormSection isLoading={isLoading} />
                  <SolarArraysFormSection isLoading={isLoading} />
                </RequirePalmettoFinanceUser>
                {productionDifferenceIfExceedsMargin !== 0 && (
                  <Box>
                    <AlertProductionDifference productionDifference={productionDifferenceIfExceedsMargin} />
                  </Box>
                )}
              </FormSectionContainer>

              <Box
                direction={{
                  base: 'column',
                  tablet: 'row',
                }}
                alignItems={{
                  base: 'stretch',
                  tablet: 'flex-end',
                }}
                justifyContent={{
                  tablet: 'flex-end',
                }}
                childGap="sm"
                style={{ flexShrink: 0 }}
                padding={{ base: 'lg', desktop: 'lg 0 0 0' }}
              >
                <Button
                  as="a"
                  navigate={() => navigate(`/accounts/${id ?? ''}`)}
                  variant="secondary"
                  tone="neutral"
                  size="md"
                  isLoading={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  size="md"
                  variant="primary"
                  type="submit"
                  isLoading={isSubmitting}
                  isDisabled={!allowsAction}
                >
                  Save
                </Button>
              </Box>
            </EnhancedFormikForm>
          )}
        </Formik>
      </Box>
      <RequirePalmettoFinanceUser>
        <RequirePermissions permissions={[UserPermissions.lightReachQCTech]}>
          <AiAssistantButton
            toggleDrawer={toggleModelOutputsDrawer}
            accountId={id}
            isDrawerOpen={isModelOutputsDrawerOpen}
          />
          <AiAssistantDrawer accountId={id} isOpen={isModelOutputsDrawerOpen} onClose={closeModelOutputsDrawer} />
        </RequirePermissions>
      </RequirePalmettoFinanceUser>
    </Box>
  );
};

export default PVSystem;
